export type OverrideCommissionBasis = 'vendorRevenue' | 'roomNights';

export interface OverrideCommissionTier {
  // Only the first tier states its own floor; the rest pick up above the previous ceiling.
  from?: number;
  to: number | null;
  commissionPct: number;
}

export interface OverrideCommissionInput {
  basis: OverrideCommissionBasis;
  // The tier metric is read in the contract currency, but the commission is paid against AUD revenue.
  vendorRevenueContract: number;
  vendorRevenueAud: number;
  roomNights: number;
  tiers?: ReadonlyArray<OverrideCommissionTier>;
}

export function computeOverrideCommission(input: OverrideCommissionInput): { pct: number; amountAud: number } {
  const { basis, vendorRevenueContract, vendorRevenueAud, roomNights, tiers } = input;

  if (!tiers?.length) {
    return { pct: 0, amountAud: 0 };
  }

  const metric = basis === 'vendorRevenue' ? vendorRevenueContract : roomNights;
  const matched = tiers.find((tier, index) => {
    const to = tier.to ?? Number.POSITIVE_INFINITY;
    if (index === 0) {
      return metric >= (tier.from ?? 0) && metric <= to;
    }

    // Exclusive, not `to + 1`: revenue is unrounded, so that floor would skip the band between them.
    const previousCeiling = tiers[index - 1].to ?? Number.POSITIVE_INFINITY;

    return metric > previousCeiling && metric <= to;
  });

  const pct = matched?.commissionPct ?? 0;

  return { pct, amountAud: (vendorRevenueAud * pct) / 100 };
}
